import { cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { CUSTOM_PRESET_ID } from "@/features/dashboard/lib/explore-date-presets";

import { describeSetDateWindow } from "../../../lib/set-date-window-display";

import type { ExtraRollingPresetMatch } from "@/features/dashboard/lib/explore-date-presets";

export interface SetDateWindowMetaProps {
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  extraRollingPresets?: readonly ExtraRollingPresetMatch[];
  today?: Date;
  className?: string;
  /**
   * `plain` stacks the preset and range flush left, with no chip inset.
   * `table` keeps one line: the range, then the preset when it is not Custom.
   * A second line would make only some rows taller.
   */
  variant?: "chip" | "plain" | "table";
}

export function SetDateWindowMeta({
  startDate,
  endDate,
  extraRollingPresets,
  today,
  className,
  variant = "chip",
}: SetDateWindowMetaProps) {
  const { t } = useTranslation();
  const display = describeSetDateWindow(startDate, endDate, t, extraRollingPresets, today);
  if (!display) return null;

  if (variant === "table") {
    const hasNamedPreset = display.presetId !== CUSTOM_PRESET_ID;
    return (
      <div
        className={cn("flex min-w-0 items-baseline gap-1.5 whitespace-nowrap", className)}
        title={hasNamedPreset ? `${display.presetLabel} · ${display.rangeLabel}` : display.rangeLabel}
      >
        <span className="min-w-0 truncate text-xs tabular-nums text-foreground">{display.rangeLabel}</span>
        {hasNamedPreset ? (
          <span className="shrink-0 text-[11px] text-muted-foreground">{display.presetLabel}</span>
        ) : null}
      </div>
    );
  }

  if (variant === "plain") {
    return (
      <div className={cn("flex min-w-0 flex-col gap-0.5 text-[11px] leading-4 text-muted-foreground", className)}>
        <span className="truncate">{display.presetLabel}</span>
        <span className="truncate">{display.rangeLabel}</span>
      </div>
    );
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-0.5", className)}>
      <span
        className="inline-flex w-fit max-w-full items-center truncate rounded-md bg-muted/70 px-1.5 py-0.5 text-[10px] font-medium leading-tight text-muted-foreground/80"
        title={`${display.presetLabel} · ${display.rangeLabel}`}
      >
        {display.presetLabel}
      </span>
      <span className="truncate text-[11px] leading-tight text-muted-foreground/55">
        {display.rangeLabel}
      </span>
    </div>
  );
}
